import { type FocusEvent, useEffect, useRef } from "react";
import { SystemState } from "../../shared/components/SystemState";
import { useSupplierPortalWorkspace } from "./portal/useSupplierPortalWorkspace";
import { labels, money } from "./portal/supplier-portal-model";

export default function SupplierOfferPortal() {
  const { workspace, communications, error, busy, success, currency, setCurrency, delivery, setDelivery, payment, setPayment, validityDate, setValidityDate, note, setNote, prices, setPrices, costs, setCosts, declineReason, setDeclineReason, questionBody, setQuestionBody, save, submit, decline, upload, askQuestion } = useSupplierPortalWorkspace();
  const portalRef = useRef<HTMLElement>(null),
    actionBarRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const bar = actionBarRef.current,
      portal = portalRef.current;
    if (!bar || !portal) return;
    const update = () => {
      portal.style.setProperty(
        "--portal-action-height",
        `${Math.ceil(bar.getBoundingClientRect().height)}px`,
      );
      // Mobil klavye ve metin büyütme görünür çalışma alanını değiştirebilir.
      // Dock, scroll alanının dışında kaldığı için içerik hiçbir zaman altına akmaz.
      const viewportHeight =
        window.visualViewport?.height ?? window.innerHeight;
      portal.style.setProperty(
        "--portal-viewport-height",
        `${Math.floor(viewportHeight)}px`,
      );
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(bar);
    window.visualViewport?.addEventListener("resize", update);
    window.visualViewport?.addEventListener("scroll", update);
    window.addEventListener("resize", update);
    return () => {
      observer.disconnect();
      window.visualViewport?.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [workspace]);
  const keepFocusedControlVisible = (event: FocusEvent<HTMLFormElement>) => {
    const bar = actionBarRef.current,
      target = event.target as HTMLElement;
    if (!bar) return;
    requestAnimationFrame(() => {
      const targetBox = target.getBoundingClientRect(),
        barBox = bar.getBoundingClientRect();
      if (targetBox.bottom > barBox.top - 12 || targetBox.top < 0)
        target.scrollIntoView({ block: "center", behavior: "auto" });
    });
  };
  if (error && !workspace)
    return (
      <SystemState
        mode="page"
        tone="danger"
        title="Teklif bağlantısı açılamadı"
        description={error}
        primaryAction={{
          label: "Yeniden dene",
          onClick: () => location.reload(),
        }}
        assistance="Bağlantının süresi dolmuşsa Satın Alma ekibinden yeni bir davet bağlantısı isteyin."
      />
    );
  if (!workspace)
    return (
      <SystemState
        mode="page"
        tone="loading"
        title="Teklif hazırlanıyor"
        description="Talep ve varsa kayıtlı taslağınız yükleniyor."
      />
    );
  const expired = new Date(workspace.rfq.deadlineAt) <= new Date(),
    serverTotal = workspace.draft?.serverGrandTotal;
  return (
    <main className="supplier-portal" ref={portalRef}>
      <header className="portal-header">
        <div className="portal-header-inner">
          <div className="portal-brand">
            <img src="/anl-logo-new-2026.webp" alt="ANL Gıda" />
            <span>Teklif talebi</span>
          </div>
          <div className="portal-request-heading">
            <div>
              <h1>{workspace.rfq.title}</h1>
              <p className="portal-request-summary">
                {workspace.rfq.rfqNo} · Son teklif zamanı: {" "}
                {new Date(workspace.rfq.deadlineAt).toLocaleString("tr-TR", {
                  timeZone: workspace.rfq.timezone,
                })}
              </p>
            </div>
            <span className="portal-status">
              {
                labels[
                  expired
                    ? "EXPIRED"
                    : workspace.offerStatus === "NOT_STARTED"
                      ? "DRAFT"
                      : workspace.offerStatus
                ]
              }
            </span>
          </div>
        </div>
      </header>
      <form onSubmit={save} onFocusCapture={keepFocusedControlVisible}>
        <div className="supplier-portal-scroll">
          <section className="portal-pricing-section">
            <div className="portal-section-heading">
              <div>
                <h2>Talep kalemleri</h2>
              </div>
              <span>{workspace.rfq.items.length} kalem</span>
            </div>
            <div className="portal-items">
              {workspace.rfq.items.map((item) => {
                const saved = workspace.draft?.items.find(
                  (i) => i.rfqItemId === item.id,
                );
                return (
                  <article key={item.id}>
                    <div>
                      <strong>
                        {item.lineNo}. {item.description}
                      </strong>
                      <small>
                        {item.itemCode} · {item.quantity} {item.unit}
                      </small>
                    </div>
                    <label className="portal-price-field">
                      Birim fiyat · {currency}
                      <input
                        inputMode="decimal"
                        min="0.0001"
                        step="0.0001"
                        required
                        value={prices[item.id] ?? ""}
                        onChange={(e) =>
                          setPrices((p) => ({
                            ...p,
                            [item.id]: e.target.value,
                          }))
                        }
                      />
                    </label>
                    <div className="portal-line-total">
                      <span>Satır toplamı</span>
                      <strong>
                        {saved ? money(saved.serverTotal, currency) : "—"}
                      </strong>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
          <section className="portal-terms">
            <div className="portal-section-heading">
              <div>
                <h2>Ticari koşullar</h2>
              </div>
            </div>
            <label>
              Para birimi
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
              >
                <option>TRY</option>
                <option>EUR</option>
                <option>USD</option>
              </select>
            </label>
            <label>
              Teslimat süresi (gün)
              <input
                type="number"
                min="0"
                required
                value={delivery}
                onChange={(e) => setDelivery(Number(e.target.value))}
              />
            </label>
            <label>
              Ödeme vadesi (gün)
              <input
                type="number"
                min="0"
                required
                value={payment}
                onChange={(e) => setPayment(Number(e.target.value))}
              />
            </label>
            <label>
              Teklif geçerlilik tarihi
              <input
                type="date"
                required
                value={validityDate}
                onChange={(e) => setValidityDate(e.target.value)}
              />
            </label>
            {(
              [
                ["shipping", "Nakliye"],
                ["insurance", "Sigorta"],
                ["installation", "Kurulum"],
                ["other", "Diğer"],
              ] as const
            ).map(([field, label]) => (
              <label key={field}>
                {label} maliyeti
                <input
                  inputMode="decimal"
                  min="0"
                  step="0.0001"
                  value={costs[field]}
                  onChange={(e) =>
                    setCosts((c) => ({ ...c, [field]: e.target.value }))
                  }
                />
              </label>
            ))}
            <p className="portal-note">
              Ek maliyetler aynı para biriminde sunucu toplamına eklenir.
            </p>
            <label className="portal-note">
              Not
              <textarea
                maxLength={2000}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </label>
          </section>
          <section className="portal-documents">
            <div className="portal-section-heading">
              <div>
                <h2>Teklif belgeleri</h2>
                <p>PDF, PNG veya JPEG formatında destekleyici belge ekleyin.</p>
              </div>
            </div>
            <label>
              Belge seç
              <input
                type="file"
                accept=".pdf,.png,.jpg,.jpeg"
                disabled={busy || expired}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void upload(file);
                  e.target.value = "";
                }}
              />
            </label>
            {workspace.draft?.documents.length ? (
              <ul>
                {workspace.draft.documents.map((d) => (
                  <li key={d.id}>
                    {d.originalName} ·{" "}
                    {d.securityStatus === "PENDING"
                      ? "Taranıyor"
                      : d.securityStatus === "CLEAN"
                        ? "Güvenli"
                        : d.securityStatus === "BLOCKED"
                          ? "Engellendi"
                          : "Tarama başarısız"}
                  </li>
                ))}
              </ul>
            ) : (
              <p>Henüz belge yüklenmedi.</p>
            )}
          </section>
          <section className="portal-decline">
            <h2>Katılmayacağınızı bildirin</h2>
            <label>
              Gerekçe
              <textarea
                minLength={10}
                maxLength={500}
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
              />
            </label>
            <button
              type="button"
              disabled={busy || expired || declineReason.trim().length < 10}
              onClick={decline}
            >
              Katılmayacağımı bildir
            </button>
          </section>
          <section className="portal-communications">
            <div>
              <h2>Soru ve duyurular</h2>
              <p>
                Sorunuz yalnız Satın Alma ekibi tarafından görülür. Ortak
                yanıtlar tedarikçi adı paylaşılmadan yayımlanır.
              </p>
            </div>
            <label>
              Satın Alma ekibine sorun
              <textarea
                minLength={10}
                maxLength={2000}
                value={questionBody}
                onChange={(event) => setQuestionBody(event.target.value)}
              />
            </label>
            <button
              type="button"
              className="secondary-button"
              disabled={busy || questionBody.trim().length < 10}
              onClick={askQuestion}
            >
              Soruyu gönder
            </button>
            {communications.announcements.length ? (
              <div className="portal-announcement-list" aria-label="Duyurular">
                {communications.announcements.map((announcement) => (
                  <article key={announcement.id}>
                    <strong>
                      {announcement.visibility === "PRIVATE"
                        ? "Size özel yanıt"
                        : "Genel duyuru"}
                    </strong>
                    <p>{announcement.body}</p>
                    <time>
                      {new Date(announcement.createdAt).toLocaleString("tr-TR")}
                    </time>
                  </article>
                ))}
              </div>
            ) : (
              <p>Henüz yayımlanmış duyuru bulunmuyor.</p>
            )}
            {communications.questions.length ? (
              <div className="portal-question-list" aria-label="Sorularınız">
                {communications.questions.map((question) => (
                  <article key={question.id}>
                    <strong>{question.status === "ANSWERED" ? "Yanıtlandı" : "Yanıt bekliyor"}</strong>
                    <p>{question.body}</p>
                    {question.answer ? <blockquote>{question.answer.body}</blockquote> : null}
                  </article>
                ))}
              </div>
            ) : null}
          </section>
          {workspace.history.length ? (
            <section>
              <h2>Gönderim geçmişiniz</h2>
              {workspace.history.map((i) => (
                <p key={i.id}>
                  Sürüm {i.version} · {money(i.grandTotal, i.currency)} ·{" "}
                  {new Date(i.submittedAt).toLocaleString("tr-TR")}
                </p>
              ))}
            </section>
          ) : null}
          {error ? (
            <p className="portal-message error" role="alert">
              {error}
            </p>
          ) : null}
          {success ? (
            <p className="portal-message success" role="status">
              {success}
            </p>
          ) : null}
        </div>
        <footer ref={actionBarRef} aria-label="Teklif işlemleri">
          <div>
            <span>Sunucunun kaydettiği teklif toplamı</span>
            <strong>
              {serverTotal == null
                ? "Taslağı kaydedince hesaplanır"
                : money(serverTotal, currency)}
            </strong>
          </div>
          <button type="submit" disabled={busy || expired}>
            Taslağı kaydet
          </button>
          <button
            type="button"
            className="primary-button"
            disabled={busy || expired}
            onClick={submit}
          >
            {busy ? "İşleniyor…" : "Teklifi gönder"}
          </button>
        </footer>
      </form>
    </main>
  );
}
